import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import tagRepository from '@/features/tags/repositories/TagRepository';
import { useTagStore } from '@/features/tags/store/useTagStore';
import { invalidatePageQueries, invalidateTagQueries } from '@/shared/query/queryKeys';

/**
 * Tag mutations and filter actions.
 *
 * Persisted tag data is derived from pages and read through `useTagsQuery`.
 * Tag writes invalidate tag and page queries (page details included), so the
 * open page's tags refresh through its detail query.
 */
export function useTagActions() {
  const queryClient = useQueryClient();
  const toggleFilter = useTagStore(state => state.toggleFilter);
  const clearFilters = useTagStore(state => state.clearFilters);

  const invalidateTagAndPageData = useCallback(async () => {
    void invalidateTagQueries(queryClient);
    await invalidatePageQueries(queryClient);
  }, [queryClient]);

  const assignTag = useCallback(
    async (pageId: string, tag: string) => {
      await tagRepository.assignTag(pageId, tag);
      await invalidateTagAndPageData();
    },
    [invalidateTagAndPageData]
  );

  const unassignTag = useCallback(
    async (pageId: string, tag: string) => {
      await tagRepository.unassignTag(pageId, tag);
      await invalidateTagAndPageData();
    },
    [invalidateTagAndPageData]
  );

  const updateTag = useCallback(
    async (oldTag: string, newTag: string) => {
      await tagRepository.editTag(oldTag, newTag);
      await invalidateTagAndPageData();
    },
    [invalidateTagAndPageData]
  );

  const deleteTag = useCallback(
    async (tag: string) => {
      await tagRepository.deleteTag(tag);
      await invalidateTagAndPageData();
    },
    [invalidateTagAndPageData]
  );

  return {
    assignTag,
    unassignTag,
    updateTag,
    deleteTag,
    toggleFilter,
    clearFilters,
  };
}
